import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Facility, Page, Prescription, PrescriptionItem } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, PageHeader, StatusBadge, Tabs } from '../../ui/components';

const TAB_KEYS = ['issue', 'dispense', 'lookup'] as const;
type TabKey = (typeof TAB_KEYS)[number];

/** e-Prescriptions — packages/modules/m24-ehealth/src/routes/prescriptions.ts. No staff-facing list endpoint exists (only /me for the citizen, /:id for a single lookup, /dispense/:qrToken for the pharmacy app) — this screen mirrors that: issue, dispense-by-QR, and single-id lookup. */
export function Prescriptions() {
  const { persona } = useAuth();
  const isClinician = persona?.roles.includes('CLINICIAN') ?? false;
  const isPharmacist = persona?.roles.includes('PHARMACIST') ?? false;
  const [tab, setTab] = useState<TabKey>(isClinician ? 'issue' : isPharmacist ? 'dispense' : 'lookup');

  return (
    <div>
      <PageHeader title="Prescriptions & Pharmacy" subtitle="Issue e-prescriptions and dispense at a registered pharmacy by QR token." />
      <Tabs
        tabs={[
          { key: 'issue', label: 'Issue prescription' },
          { key: 'dispense', label: 'Pharmacy dispense' },
          { key: 'lookup', label: 'Look up by ID' },
        ]}
        value={tab}
        onChange={setTab}
      />
      {tab === 'issue' ? <IssueTab isClinician={isClinician} /> : null}
      {tab === 'dispense' ? <DispenseTab isPharmacist={isPharmacist} /> : null}
      {tab === 'lookup' ? <LookupTab /> : null}
    </div>
  );
}

function IssueTab({ isClinician }: { isClinician: boolean }) {
  const facilitiesQ = useApiQuery<Page<Facility>>('/api/v1/m24/facilities?limit=100');
  const [nin, setNin] = useState('');
  const [encounterRef, setEncounterRef] = useState('');
  const [facilityId, setFacilityId] = useState('');
  const [items, setItems] = useState<PrescriptionItem[]>([]);
  const [drug, setDrug] = useState('');
  const [dose, setDose] = useState('');
  const [duration, setDuration] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<Prescription | null>(null);

  const addItem = () => {
    if (!drug.trim() || !dose.trim() || !duration.trim()) return;
    setItems((it) => [...it, { drug, dose, duration }]);
    setDrug('');
    setDose('');
    setDuration('');
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const p = await api.post<Prescription>('/api/v1/m24/prescriptions', { nin, encounterRef, facilityId, items });
      setIssued(p);
      setItems([]);
      setNin('');
      setEncounterRef('');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to issue prescription');
    } finally {
      setBusy(false);
    }
  };

  if (!isClinician) {
    return <ErrorNote message="Issuing a prescription requires the CLINICIAN role." />;
  }

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">New prescription</h2>
        {error ? (
          <div className="mb-3">
            <ErrorNote message={error} />
          </div>
        ) : null}
        <div className="space-y-3">
          <Field label="Patient NIN">
            <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} placeholder="e.g. SC870512033" />
          </Field>
          <Field label="Encounter reference">
            <input className="field-input" value={encounterRef} onChange={(e) => setEncounterRef(e.target.value)} placeholder="e.g. consult note ref" />
          </Field>
          <Field label="Facility">
            <select className="field-input" value={facilityId} onChange={(e) => setFacilityId(e.target.value)}>
              <option value="">Select a facility…</option>
              {(facilitiesQ.data?.items ?? []).map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </Field>
          <div>
            <div className="field-label">Items</div>
            {items.length > 0 ? (
              <ul className="mb-2 space-y-1">
                {items.map((it, i) => (
                  <li key={i} className="flex items-center justify-between rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs">
                    <span>
                      {it.drug} — {it.dose}, {it.duration}
                    </span>
                    <button className="text-slate-400 hover:text-rose-600" onClick={() => setItems((its) => its.filter((_, xi) => xi !== i))}>
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="grid grid-cols-4 gap-1.5">
              <input className="field-input" placeholder="Drug" value={drug} onChange={(e) => setDrug(e.target.value)} />
              <input className="field-input" placeholder="Dose" value={dose} onChange={(e) => setDose(e.target.value)} />
              <input className="field-input" placeholder="Duration" value={duration} onChange={(e) => setDuration(e.target.value)} />
              <button className="btn-secondary !px-2" onClick={addItem}>
                + Add
              </button>
            </div>
          </div>
          <button
            className="btn-primary w-full"
            onClick={submit}
            disabled={busy || nin.trim().length < 5 || !encounterRef.trim() || !facilityId || items.length === 0}
          >
            {busy ? 'Issuing…' : 'Issue prescription'}
          </button>
        </div>
      </div>

      <div className="card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Just issued</h2>
        {!issued ? (
          <p className="px-2 py-8 text-center text-sm text-slate-400">Issue a prescription to see its QR dispense token here.</p>
        ) : (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
            <div className="mb-1 flex items-center justify-between">
              <span className="font-semibold text-emerald-800">{issued.nin}</span>
              <StatusBadge status={issued.state} />
            </div>
            <ul className="mb-2 text-xs text-emerald-800">
              {issued.items.map((it, i) => (
                <li key={i}>
                  {it.drug} — {it.dose}, {it.duration}
                </li>
              ))}
            </ul>
            <div className="text-xs text-emerald-700">
              QR dispense token: <code className="rounded bg-white/60 px-1 py-0.5">{issued.qrToken}</code>
            </div>
            <div className="mt-1 text-xs text-emerald-700">Expires {formatDateTime(issued.expiresAt)}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function DispenseTab({ isPharmacist }: { isPharmacist: boolean }) {
  const [qrToken, setQrToken] = useState('');
  const [rx, setRx] = useState<Prescription | null>(null);
  const [pharmacyName, setPharmacyName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const lookUp = async () => {
    setBusy(true);
    setError(null);
    setRx(null);
    try {
      const p = await api.get<Prescription>(`/api/v1/m24/prescriptions/dispense/${encodeURIComponent(qrToken.trim())}`);
      setRx(p);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Lookup failed');
    } finally {
      setBusy(false);
    }
  };

  const dispense = async () => {
    setBusy(true);
    setError(null);
    try {
      const p = await api.post<Prescription>(`/api/v1/m24/prescriptions/dispense/${encodeURIComponent(qrToken.trim())}`, { pharmacyName });
      setRx(p);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Dispense failed');
    } finally {
      setBusy(false);
    }
  };

  if (!isPharmacist) {
    return <ErrorNote message="Pharmacy dispensing requires the PHARMACIST role." />;
  }

  return (
    <div className="card max-w-xl p-5">
      <h2 className="mb-3 text-sm font-bold text-slate-700">Scan / paste QR dispense token</h2>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="flex gap-2">
        <input className="field-input" value={qrToken} onChange={(e) => setQrToken(e.target.value)} placeholder="QR token…" />
        <button className="btn-secondary shrink-0" onClick={lookUp} disabled={busy || !qrToken.trim()}>
          Look up
        </button>
      </div>

      {rx ? (
        <div className="mt-4 rounded-lg border border-slate-200 p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-semibold text-slate-900">{rx.nin}</span>
            <StatusBadge status={rx.state} />
          </div>
          <ul className="mb-2 text-xs text-slate-600">
            {rx.items.map((it, i) => (
              <li key={i}>
                {it.drug} — {it.dose}, {it.duration}
              </li>
            ))}
          </ul>
          <div className="text-xs text-slate-500">Expires {formatDateTime(rx.expiresAt)}</div>
          {rx.state === 'DISPENSED' ? (
            <p className="mt-2 text-xs text-slate-500">
              Already dispensed by {rx.dispensedByPharmacy} at {formatDateTime(rx.dispensedAt)}.
            </p>
          ) : rx.state === 'EXPIRED' ? (
            <p className="mt-2 text-xs text-rose-600">This prescription has expired.</p>
          ) : (
            <div className="mt-3 flex gap-2">
              <input className="field-input" placeholder="Dispensing pharmacy name" value={pharmacyName} onChange={(e) => setPharmacyName(e.target.value)} />
              <button className="btn-success shrink-0" onClick={dispense} disabled={busy || pharmacyName.trim().length < 2}>
                Dispense
              </button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function LookupTab() {
  const [id, setId] = useState('');
  const [rx, setRx] = useState<Prescription | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const lookUp = async () => {
    setBusy(true);
    setError(null);
    setRx(null);
    try {
      setRx(await api.get<Prescription>(`/api/v1/m24/prescriptions/${encodeURIComponent(id.trim())}`));
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Lookup failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card max-w-xl p-5">
      <h2 className="mb-3 text-sm font-bold text-slate-700">Look up a prescription by id</h2>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="flex gap-2">
        <input className="field-input" value={id} onChange={(e) => setId(e.target.value)} placeholder="Prescription id…" />
        <button className="btn-secondary shrink-0" onClick={lookUp} disabled={busy || !id.trim()}>
          Look up
        </button>
      </div>
      {rx ? (
        <div className="mt-4 rounded-lg border border-slate-200 p-4 text-sm">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-semibold text-slate-900">{rx.nin}</span>
            <StatusBadge status={rx.state} />
          </div>
          <ul className="text-xs text-slate-600">
            {rx.items.map((it, i) => (
              <li key={i}>
                {it.drug} — {it.dose}, {it.duration}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
